import { client } from "@/hera-api/client/client.gen";

export type RegionGeometry = {
  type: string;
  coordinates: unknown;
};

export type JurisdictionRegion = {
  id: string;
  jurisdiction_id: string;
  name: string;
  geometry: RegionGeometry;
  source: string;
  recipe: Record<string, unknown> | null;
  enabled: boolean;
  assignment_pending: boolean;
  created_at: string;
  last_updated_at: string;
};

export type RegionUploadFeature = {
  name: string | null;
  geometry: RegionGeometry;
  attributes: Record<string, unknown>;
};

export type RegionPromptDraft = {
  name: string;
  geometry: RegionGeometry;
};

type ErrorBody = {
  detail?: unknown;
  message?: unknown;
  error_content?: { detail?: unknown; message?: unknown };
};

/** The fetch interceptor wraps bodies in a CustomFetchError whose own message is "". */
export function regionErrorMessage(error: unknown, fallback: string): string {
  const body = error as ErrorBody | null | undefined;
  for (const value of [
    body?.error_content?.detail,
    body?.error_content?.message,
    body?.detail,
    body?.message,
  ]) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return fallback;
}

async function readError(error: unknown, fallback: string): Promise<never> {
  throw new Error(regionErrorMessage(error, fallback));
}

async function getJson<T>(
  url: string,
  query: Record<string, unknown> | undefined,
  fallback: string,
): Promise<T> {
  const result = await client.get({
    url,
    query,
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) await readError(result.error, fallback);
  return result.data as T;
}

async function sendJson<T>(
  method: "post" | "patch",
  url: string,
  body: unknown,
  fallback: string,
  signal?: AbortSignal,
): Promise<T> {
  const result = await client[method]({
    url,
    body,
    signal,
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) await readError(result.error, fallback);
  return result.data as T;
}

export function listJurisdictionRegions(jurisdictionIds: string[]) {
  return getJson<{ regions: JurisdictionRegion[] }>(
    "/regions",
    { jurisdiction_ids: jurisdictionIds },
    "Failed to load regions",
  );
}

export function getJurisdictionRegionBoundary(jurisdictionId: string) {
  return getJson<{ jurisdiction_id: string; geometry: RegionGeometry }>(
    "/regions/boundary",
    { jurisdiction_id: jurisdictionId },
    "Failed to load the jurisdiction boundary",
  );
}

export type RegionSaved = {
  region: JurisdictionRegion;
  properties_assigned: number;
  assignment_pending: boolean;
};

export function savedRegionMessage(saved: RegionSaved): string {
  if (saved.assignment_pending) {
    return `Saved ${saved.region.name}. Properties will be assigned in the background.`;
  }
  if (!saved.region.enabled) {
    return `${saved.region.name} is disabled and no longer assigns properties.`;
  }
  return `Saved ${saved.region.name}.`;
}

export function createJurisdictionRegion(body: {
  jurisdiction_id: string;
  name: string;
  geometry: RegionGeometry;
  source: "drawn" | "upload" | "prompt";
  recipe?: Record<string, unknown> | null;
}) {
  return sendJson<RegionSaved>(
    "post",
    "/regions",
    body,
    "Failed to save the region",
  );
}

export function updateJurisdictionRegion(
  regionId: string,
  body: {
    jurisdiction_id: string;
    name?: string;
    geometry?: RegionGeometry;
    source?: "drawn" | "upload" | "prompt";
    recipe?: Record<string, unknown> | null;
    enabled?: boolean;
  },
) {
  return sendJson<RegionSaved>(
    "patch",
    `/regions/${regionId}`,
    body,
    "Failed to update the region",
  );
}

export async function deleteJurisdictionRegion(
  regionId: string,
  jurisdictionId: string,
) {
  const result = await client.delete({
    url: `/regions/${regionId}`,
    query: {
      jurisdiction_id: jurisdictionId,
    },
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error)
    await readError(result.error, "Failed to delete the region");
}

export async function previewRegionUpload(
  jurisdictionId: string,
  file: File,
  signal?: AbortSignal,
) {
  const body = new FormData();
  body.set("jurisdiction_id", jurisdictionId);
  body.set("file", file);
  const result = await client.post({
    url: "/regions/preview-upload",
    body,
    signal,
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error) await readError(result.error, "Failed to read the file");
  return result.data as {
    draft_id: string;
    features: RegionUploadFeature[];
    attribute_keys: string[];
  };
}

export function commitRegionUpload(body: {
  jurisdiction_id: string;
  draft_id: string;
  mode: "single" | "multi";
  name?: string;
  name_attribute?: string;
}) {
  return sendJson<{ regions: RegionSaved[] }>(
    "post",
    "/regions/commit-upload",
    body,
    "Failed to save the upload",
  );
}

export type RegionUndoStep = {
  kind:
    | "delete_regions"
    | "restore_region"
    | "recreate_region"
    | "select"
    | "show_jurisdiction";
  jurisdiction_id: string | null;
  region_id?: string | null;
  region_ids?: string[];
  name?: string | null;
  geometry?: RegionGeometry | null;
  recipe?: Record<string, unknown> | null;
  enabled?: boolean | null;
  selected_region_id?: string | null;
};

export type RegionCommandResult = {
  action: string;
  message: string;
  jurisdiction_id: string | null;
  selected_region_id: string | null;
  undo: RegionUndoStep[];
};

export function commandJurisdictionRegions(
  body: {
    prompt: string;
    jurisdiction_id?: string | null;
    selected_region_id?: string | null;
    history?: Array<{ prompt: string; reply: string }>;
  },
  signal?: AbortSignal,
) {
  return sendJson<RegionCommandResult>(
    "post",
    "/regions/command",
    body,
    "Failed to apply the region request",
    signal,
  );
}

export function promptJurisdictionRegions(body: {
  jurisdiction_id: string;
  prompt: string;
  previous_recipe?: Record<string, unknown> | null;
  draft_id?: string | null;
  region_id?: string | null;
}) {
  return sendJson<{
    summary: string;
    recipe: Record<string, unknown>;
    draft_id: string;
    regions: RegionPromptDraft[];
  }>("post", "/regions/prompt", body, "Failed to build the region");
}

export const UNASSIGNED_REGION_FILTER_ID = "unassigned";

export function regionLatLngRings(
  geometry: RegionGeometry,
): Array<Array<{ lat: number; lng: number }>> {
  const coordinates = geometry.coordinates as number[][][] | number[][][][];
  if (geometry.type === "Polygon") {
    return (coordinates as number[][][]).map((ring) =>
      ring.map(([lng, lat]) => ({ lat, lng })),
    );
  }
  if (geometry.type === "MultiPolygon") {
    return (coordinates as number[][][][]).flatMap((polygon) =>
      polygon.map((ring) => ring.map(([lng, lat]) => ({ lat, lng }))),
    );
  }
  return [];
}

export function regionQueryParams(selectedRegionIds: string[] | undefined): {
  region_ids?: string[];
  include_unassigned_regions?: boolean;
} {
  const selected = selectedRegionIds ?? [];
  const regionIds = selected.filter((id) => id !== UNASSIGNED_REGION_FILTER_ID);
  return {
    region_ids: regionIds.length > 0 ? regionIds : undefined,
    include_unassigned_regions: selected.includes(UNASSIGNED_REGION_FILTER_ID)
      ? true
      : undefined,
  };
}

export function buildRegionFilterModel(
  regions: JurisdictionRegion[],
  jurisdictionNames: Record<string, string>,
): {
  show: boolean;
  options: Array<{ value: string; label: string }>;
  catalog: Array<{ id: string; name: string }>;
} {
  const active = regions.filter((region) => region.enabled !== false);
  const jurisdictionIds = new Set(
    active.map((region) => region.jurisdiction_id),
  );
  const qualify = jurisdictionIds.size > 1;
  const options = active.map((region) => {
    const jurisdictionName = jurisdictionNames[region.jurisdiction_id];
    return {
      value: region.id,
      label:
        qualify && jurisdictionName
          ? `${region.name} (${jurisdictionName})`
          : region.name,
    };
  });
  const catalog = active.map((region) => ({
    id: region.id,
    name: region.name,
  }));
  if (active.length > 0) {
    options.push({ value: UNASSIGNED_REGION_FILTER_ID, label: "Unassigned" });
    catalog.push({ id: UNASSIGNED_REGION_FILTER_ID, name: "Unassigned" });
  }
  return { show: regions.length > 0, options, catalog };
}

async function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function downloadJurisdictionRegionShapefile(
  regionId: string,
  jurisdictionId: string,
  filename: string,
) {
  const result = await client.get({
    url: `/regions/${regionId}/shapefile`,
    query: { jurisdiction_id: jurisdictionId },
    parseAs: "blob",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error)
    await readError(result.error, "Failed to download the shapefile");
  await downloadBlob(result.data as Blob, filename);
}

export async function downloadRegionDraftShapefile(
  draftId: string,
  featureIndex: number,
  jurisdictionId: string,
  filename: string,
) {
  const result = await client.get({
    url: `/regions/drafts/${draftId}/features/${featureIndex}/shapefile`,
    query: { jurisdiction_id: jurisdictionId },
    parseAs: "blob",
    security: [{ scheme: "bearer", type: "http" }],
  });
  if (result.error)
    await readError(result.error, "Failed to download the shapefile");
  await downloadBlob(result.data as Blob, filename);
}

export function formatRegionNames(names: string[] | null | undefined): string {
  if (!names || names.length === 0) return "Unassigned";
  return [...names].sort((left, right) => left.localeCompare(right)).join(", ");
}

export function regionNamesMatchFilter(
  names: string[] | null | undefined,
  selected: readonly string[],
): boolean {
  const cell = names && names.length > 0 ? names : ["Unassigned"];
  return selected.some((value) =>
    cell.some((name) => regionDisplayMatches(name, value)),
  );
}

function regionDisplayMatches(cellName: string, selected: string): boolean {
  const bare = selected.replace(/ \([^)]*\)$/, "");
  return (
    cellName.localeCompare(selected, undefined, { sensitivity: "accent" }) ===
      0 ||
    cellName.localeCompare(bare, undefined, { sensitivity: "accent" }) === 0
  );
}
